import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import Webcam from "react-webcam";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useChat,
  useTracks,
  useLocalParticipant,
  useSpeakingParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { validateHybridVerificationCode } from "../../Redux/ActionCreators/videoInterviewAction";
import HybridVideoInterviewThankYou from "./HybridVideoInterviewThankYou";
import "./HybridVideoInterviewMain.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getRoleFromIdentity = (identity = "") => {
  const m = identity.match(/^(candidate|recruiter|panelist|agent)/i);
  return m ? m[1].toLowerCase() : null;
};

const isAgentIdentity = (identity = "") => /^agent/i.test(identity);

const getDisplayName = (participant) => {
  const identity = participant?.identity || "";
  if (isAgentIdentity(identity)) return "Gappeo AI";
  const name = (participant?.name || "")
    .replace(/\s*\((?:candidate|recruiter|panelist|agent)\)\s*$/i, "")
    .trim();
  if (name) return name;
  return identity.replace(/^(?:candidate|recruiter|panelist|agent)[-_\s]*/i, "").trim() || identity;
};


const getGridStyle = (count) => {
  if (count <= 1) return { gridTemplateColumns: "1fr", gridTemplateRows: "1fr" };
  if (count === 2) return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr" };
  if (count <= 4) return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
  return { gridTemplateColumns: "repeat(3, 1fr)" };
};

// ─── Single participant tile ──────────────────────────────────────────────────

const ParticipantTileItem = ({ trackRef, isSpeaking }) => {
  const { participant } = trackRef;
  const identity = participant?.identity || "";
  const isAgent = isAgentIdentity(identity);
  const displayName = getDisplayName(participant);
  const hasVideo = !!trackRef.publication && !trackRef.publication.isMuted;
  const isScreenShare = trackRef.source === Track.Source.ScreenShare;
  const avatarInitial = isAgent ? "G" : (displayName[0] || "?").toUpperCase();

  return (
    <div className={`pt-tile${isSpeaking && !isScreenShare ? " is-speaking" : ""}`}>
      {hasVideo ? (
        <VideoTrack trackRef={trackRef} className="pt-video" />
      ) : (
        <div className="pt-avatar">
          <span>{avatarInitial}</span>
        </div>
      )}
      {isScreenShare ? (
        <div className="pt-screen-label">
          <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
            <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 16V6h16v10H4z" />
          </svg>
          <span>{displayName}&apos;s screen</span>
        </div>
      ) : (
        <div className="pt-info-bar">
          {isSpeaking && <span className="pt-speaking-dot" />}
          <span className="pt-name">{displayName}</span>
        </div>
      )}
    </div>
  );
};

// ─── Inner room component (must live inside <LiveKitRoom>) ───────────────────

const HybridRoomContent = ({ connectionDetails, onLeave }) => {
  const myIdentity = connectionDetails?.live_kit?.participant_name || "";
  const myRole = getRoleFromIdentity(myIdentity);
  const isCandidate = myRole === "candidate";

  const [chatOpen, setChatOpen] = useState(myRole === "recruiter" || myRole === "panelist");
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef(null);
  const { chatMessages, send } = useChat();
  const {
    localParticipant,
    isMicrophoneEnabled,
    isScreenShareEnabled,
  } = useLocalParticipant();

  const speakingParticipants = useSpeakingParticipants();
  const speakingIds = new Set(speakingParticipants.map((p) => p.identity));

  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }
  );
  const screenTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false }
  );

  const hasScreenShares = screenTracks.length > 0;

  const isAgentParticipant = (msg) => {
    try {
      const meta = JSON.parse(msg.from?.metadata || "{}");
      if (meta.role === "agent") return true;
    } catch {}
    return (msg.from?.identity || "").toLowerCase().includes("agent");
  };

  const visibleMessages = isCandidate
    ? chatMessages.filter((msg) => !isAgentParticipant(msg))
    : chatMessages;

  useEffect(() => {
    if (chatOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [visibleMessages, chatOpen]);

  const handleSendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    send(chatInput.trim());
    setChatInput("");
  };

  const titleText =
    connectionDetails?.video_call?.job?.title ||
    connectionDetails?.video_call?.title ||
    "Interview";

  return (
    <div className="hybrid-room">
      {/* Header */}
      <div className="hybrid-room-header">
        <img src="/images/gappeoai-logo.png" alt="logo" />
        <h1>{titleText}</h1>
        <div className="header-right">
          {myRole && <span className={`role-badge role-${myRole}`}>{myRole}</span>}
        </div>
      </div>

      {/* Body */}
      <div className="hybrid-room-body">
        {hasScreenShares ? (
          <>
            {/* Featured screen-share layout */}
            <div className="hybrid-featured-main">
              <div className={`screen-share-grid ss-count-${Math.min(screenTracks.length, 4)}`}>
                {screenTracks.map((trackRef, i) => (
                  <ParticipantTileItem
                    key={`${trackRef.participant?.identity}-screen-${i}`}
                    trackRef={trackRef}
                    isSpeaking={speakingIds.has(trackRef.participant?.identity)}
                  />
                ))}
              </div>
            </div>
            {/* Camera thumbnail strip on the right */}
            <div className="hybrid-camera-strip">
              {cameraTracks.map((trackRef, i) => (
                <ParticipantTileItem
                  key={`${trackRef.participant?.identity}-cam-${i}`}
                  trackRef={trackRef}
                  isSpeaking={speakingIds.has(trackRef.participant?.identity)}
                />
              ))}
            </div>
          </>
        ) : (
          /* Standard camera grid */
          <div className="hybrid-grid-area">
            <div className="participant-grid" style={getGridStyle(cameraTracks.length)}>
              {cameraTracks.map((trackRef, i) => (
                <ParticipantTileItem
                  key={`${trackRef.participant?.identity}-${trackRef.source}-${i}`}
                  trackRef={trackRef}
                  isSpeaking={speakingIds.has(trackRef.participant?.identity)}
                />
              ))}
            </div>
          </div>
        )}

        {chatOpen && (
          <div className="hybrid-chat-panel">
            <div className="chat-header">
              <span>Chat</span>
              <button onClick={() => setChatOpen(false)}>✕</button>
            </div>
            <div className="chat-messages">
              {visibleMessages.length === 0 && (
                <p className="chat-empty">No messages yet</p>
              )}
              {visibleMessages.map((msg, i) => {
                const isAgent = isAgentParticipant(msg);
                return (
                  <div
                    key={msg.id || i}
                    className={`chat-msg ${isAgent ? "chat-msg-agent" : ""}`}
                  >
                    <span className="chat-sender">
                      {isAgent
                        ? "AI Suggestion"
                        : msg.from?.name || msg.from?.identity || "Participant"}
                    </span>
                    <span className="chat-text">
                      {msg.message.split(/\\n|\n/).map((line, idx, arr) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            <form className="chat-input-row" onSubmit={handleSendChat}>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message…"
              />
              <button type="submit">Send</button>
            </form>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="hybrid-controls-bar">
        <RoomAudioRenderer />

        {/* Microphone */}
        <button
          className={`ctrl-btn ${!isMicrophoneEnabled ? "ctrl-btn-off" : ""}`}
          onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)}
          title={isMicrophoneEnabled ? "Mute microphone" : "Unmute microphone"}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            {isMicrophoneEnabled ? (
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z" />
            ) : (
              <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
            )}
          </svg>
          <span>{isMicrophoneEnabled ? "Mute" : "Unmute"}</span>
        </button>

        {/* Screen share */}
        <button
          className={`ctrl-btn ${isScreenShareEnabled ? "active" : ""}`}
          onClick={() => localParticipant.setScreenShareEnabled(!isScreenShareEnabled)}
          title={isScreenShareEnabled ? "Stop sharing" : "Share screen"}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 16V6h16v10H4z" />
            <path d="M8 13l4-4 4 4" />
          </svg>
          <span>{isScreenShareEnabled ? "Stop share" : "Share screen"}</span>
        </button>

        {/* Chat */}
        <button
          className={`ctrl-btn chat-toggle-btn ${chatOpen ? "active" : ""}`}
          onClick={() => setChatOpen((p) => !p)}
          title="Chat"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
          <span>Chat</span>
          {!isCandidate && visibleMessages.filter(isAgentParticipant).length > 0 && (
            <span className="chat-badge">AI</span>
          )}
        </button>

        {/* Leave */}
        <button className="ctrl-btn leave-ctrl-btn" onClick={onLeave} title="Leave">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
          <span>Leave</span>
        </button>
      </div>
    </div>
  );
};

// ─── Pre-join screen ──────────────────────────────────────────────────────────

const PrejoinScreen = ({
  videoCallId,
  onJoined,
  hybridValidating,
  snackbar,
  setSnackbar,
}) => {
  const dispatch = useDispatch();
  const webcamRef = useRef(null);
  const micStreamRef = useRef(null);

  const [cameraGranted, setCameraGranted] = useState(false);
  const [micGranted, setMicGranted] = useState(false);
  const [speakerGranted, setSpeakerGranted] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [videoDevices, setVideoDevices] = useState([]);

  const canVerify = cameraGranted && micGranted && speakerGranted;

  const handleGrantCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (webcamRef.current) webcamRef.current.srcObject = stream;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      setVideoDevices(videoInputs);
      setSelectedDeviceId(videoInputs[0]?.deviceId || "");
      setCameraGranted(true);
    } catch {
      setSnackbar({
        open: true,
        message: "Camera access denied. Please allow camera access.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    handleGrantCamera();
    return () => {
      if (micStreamRef.current)
        micStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGrantMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      setMicGranted(true);
    } catch {
      setSnackbar({
        open: true,
        message: "Microphone access denied. Please allow mic access.",
        severity: "error",
      });
    }
  };

  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      setSnackbar({ open: true, message: "Please enter your verification code.", severity: "error" });
      return;
    }
    const result = await dispatch(
      validateHybridVerificationCode(videoCallId, verificationCode)
    );
    if (result.success) {
      onJoined();
    } else {
      setSnackbar({
        open: true,
        message: result.message || "Invalid verification code",
        severity: "error",
      });
    }
  };

  return (
    <div className="hybrid-prejoin">
      <div className="hybrid-prejoin-body">

        {/* Left – camera preview */}
        <div className="prejoin-camera-side">
          <div className="cam-preview-area">
            {cameraGranted ? (
              <div className="cam-preview-wrapper">
                <Webcam
                  ref={webcamRef}
                  mirrored
                  audio={false}
                  className="prejoin-webcam"
                  videoConstraints={
                    selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : undefined
                  }
                />
                {videoDevices.length > 1 && (
                  <select
                    className="cam-device-select"
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                  >
                    {videoDevices.map((d, i) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Camera ${i + 1}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <div className="cam-placeholder">
                <img src="/images/video-camera-black.svg" alt="camera" />
                <p>Camera is off</p>
              </div>
            )}
          </div>

          <div className="prejoin-device-controls">
            {/* Camera */}
            <div className="device-row">
              <div className="device-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z" />
                </svg>
              </div>
              <div className="device-info">
                <span>Camera</span>
                {cameraGranted ? (
                  <span className="confirmed-text">✓ Allowed</span>
                ) : (
                  <button className="grant-btn" onClick={handleGrantCamera}>Allow</button>
                )}
              </div>
              <div className={`perm-dot ${cameraGranted ? "granted" : "denied"}`} />
            </div>

            {/* Mic */}
            <div className="device-row">
              <div className="device-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z" />
                </svg>
              </div>
              <div className="device-info">
                <span>Microphone</span>
                {micGranted ? (
                  <span className="confirmed-text">✓ Allowed</span>
                ) : (
                  <button className="grant-btn" onClick={handleGrantMic}>Allow</button>
                )}
              </div>
              <div className={`perm-dot ${micGranted ? "granted" : "denied"}`} />
            </div>

            {/* Speaker */}
            <div className="device-row">
              <div className="device-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                </svg>
              </div>
              <div className="device-info">
                <span>Speaker</span>
                {speakerGranted ? (
                  <span className="confirmed-text">✓ Allowed</span>
                ) : (
                  <button className="grant-btn" onClick={() => setSpeakerGranted(true)}>Allow</button>
                )}
              </div>
              <div className={`perm-dot ${speakerGranted ? "granted" : "denied"}`} />
            </div>
          </div>
        </div>

        {/* Right – OTP panel */}
        <div className="prejoin-otp-side">
          <div className="prejoin-card">
            <img src="/images/gappeoai-logo.png" alt="logo" className="prejoin-logo" />
            <h2>Join Interview</h2>
            <p className="prejoin-subtitle">
              Enter your verification code to join the session
            </p>

            <div className="perm-status-list">
              <div className={`perm-status-item ${cameraGranted ? "ok" : "warn"}`}>
                <span>{cameraGranted ? "✓" : "!"}</span> Camera{" "}
                {cameraGranted ? "allowed" : "not allowed"}
              </div>
              <div className={`perm-status-item ${micGranted ? "ok" : "warn"}`}>
                <span>{micGranted ? "✓" : "!"}</span> Microphone{" "}
                {micGranted ? "allowed" : "not allowed"}
              </div>
              <div className={`perm-status-item ${speakerGranted ? "ok" : "warn"}`}>
                <span>{speakerGranted ? "✓" : "!"}</span> Speaker{" "}
                {speakerGranted ? "allowed" : "not allowed"}
              </div>
            </div>

            <input
              className="otp-input"
              type="text"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && canVerify && !hybridValidating && handleVerify()
              }
            />

            <button
              className="verify-btn"
              onClick={handleVerify}
              disabled={!canVerify || hybridValidating}
            >
              {hybridValidating ? "Verifying…" : "Verify & Join"}
            </button>

            {!canVerify && (
              <p className="verify-hint">
                Allow camera, microphone and speaker to enable this button.
              </p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

// ─── Main exported component ─────────────────────────────────────────────────

const HybridVideoInterviewMain = () => {
  const { token: videoCallId } = useParams();
  const { hybridValidating, hybridConnectionDetails } = useSelector(
    (state) => state.videoInterview
  );

  const [joined, setJoined] = useState(false);
  const [left, setLeft] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const myRole = hybridConnectionDetails
    ? getRoleFromIdentity(hybridConnectionDetails.live_kit?.participant_name || "")
    : null;

  const handleLeave = () => setLeft(true);

  if (left) {
    return <HybridVideoInterviewThankYou role={myRole} />;
  }

  return (
    <div className="hybrid-wrapper">
      {!joined ? (
        <PrejoinScreen
          videoCallId={videoCallId}
          onJoined={() => setJoined(true)}
          hybridValidating={hybridValidating}
          snackbar={snackbar}
          setSnackbar={setSnackbar}
        />
      ) : hybridConnectionDetails ? (
        <LiveKitRoom
          serverUrl={hybridConnectionDetails.live_kit.server_url}
          token={hybridConnectionDetails.live_kit.participant_token}
          connect={true}
          video={true}
          audio={true}
          onDisconnected={handleLeave}
        >
          <HybridRoomContent
            connectionDetails={hybridConnectionDetails}
            onLeave={handleLeave}
          />
        </LiveKitRoom>
      ) : (
        <div className="hybrid-loading">Loading session…</div>
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default HybridVideoInterviewMain;
